import axios from 'axios';
import {
  IMAGE_TYPE_CATEGORY_OPTIONS,
  formatImageTypeSize,
} from '@frontend/constants/system-image-types';
import { listImageTypes } from '@frontend/services/imageTypesApi';

let cachedTypes = null;
let cachedCatalogKey = null;
let typesInflight = null;
let typesInflightKey = null;

let cachedSequences = null;
let cachedSequencesKey = null;
let sequencesInflight = null;
let sequencesInflightKey = null;

const SKIP_TOAST = { skipErrorToast: true };

export function getShareAccessTokenFromLocation() {
  if (typeof window === 'undefined') {
    return '';
  }
  const match = window.location.pathname.match(/^\/briefs\/share\/([^/?#]+)/);
  return match && match[1] ? decodeURIComponent(match[1]) : '';
}

/**
 * Org slug for guest intake / vanity start flows (no auth session).
 * Prefer path on /briefs/start/{slug}/…; otherwise ?external=1&slug=.
 */
export function getIntakeOrgSlugFromLocation() {
  if (typeof window === 'undefined') {
    return '';
  }
  const startMatch = window.location.pathname.match(/^\/briefs\/start\/([^/?#]+)/);
  if (startMatch && startMatch[1]) {
    return decodeURIComponent(startMatch[1]);
  }
  const params = new URLSearchParams(window.location.search);
  if (params.get('external') === '1') {
    const slug = (params.get('slug') || '').trim();
    if (slug) {
      return slug;
    }
  }
  return '';
}

function resolveCatalogKey(shareToken, intakeSlug) {
  if (shareToken) {
    return `share:${shareToken}`;
  }
  if (intakeSlug) {
    return `intake:${intakeSlug}`;
  }
  return 'auth';
}

export async function loadAllImageTypes({
  force = false,
  shareToken = null,
  intakeSlug = null,
} = {}) {
  const token = shareToken != null ? String(shareToken).trim() : getShareAccessTokenFromLocation();
  const slug = intakeSlug != null
    ? String(intakeSlug).trim()
    : (token ? '' : getIntakeOrgSlugFromLocation());
  const catalogKey = resolveCatalogKey(token || '', slug || '');
  if (!force && cachedTypes && cachedCatalogKey === catalogKey) {
    return cachedTypes;
  }
  if (!force && typesInflight && typesInflightKey === catalogKey) {
    return typesInflight;
  }
  const request = (async () => {
    try {
      let types = [];
      if (token) {
        const res = await axios.get(
          `/briefs/api/share/${encodeURIComponent(token)}/image-types`,
          SKIP_TOAST,
        );
        types = Array.isArray(res.data) ? res.data : [];
      } else if (slug) {
        const res = await axios.get(
          `/briefs/api/intake/${encodeURIComponent(slug)}/image-types`,
          SKIP_TOAST,
        );
        types = Array.isArray(res.data) ? res.data : [];
      } else {
        types = await listImageTypes();
      }
      cachedTypes = types;
      cachedCatalogKey = catalogKey;
      return types;
    } catch {
      return cachedTypes || [];
    }
  })();
  typesInflightKey = catalogKey;
  typesInflight = request;
  request.finally(() => {
    if (typesInflight === request) {
      typesInflight = null;
      typesInflightKey = null;
    }
  });
  return request;
}

export function peekCachedImageTypes() {
  return cachedTypes;
}

export function clearImageTypesCache() {
  cachedTypes = null;
  cachedCatalogKey = null;
  typesInflight = null;
  typesInflightKey = null;
}

export function groupImageTypesByCategory(types) {
  const groups = {};
  (types || []).forEach((type) => {
    const cat = type.category == null ? '' : String(type.category).trim();
    if (!groups[cat]) {
      groups[cat] = [];
    }
    groups[cat].push(type);
  });

  const categoryOrder = IMAGE_TYPE_CATEGORY_OPTIONS.map((c) => c.value);
  const categoryLabels = Object.fromEntries(
    IMAGE_TYPE_CATEGORY_OPTIONS.map((c) => [c.value, c.label]),
  );

  const ordered = categoryOrder
    .filter((cat) => groups[cat]?.length)
    .map((cat) => ({
      category: cat,
      label: `${categoryLabels[cat] || cat} image type`,
      types: groups[cat].sort((a, b) => a.name.localeCompare(b.name)),
    }));

  Object.keys(groups).forEach((cat) => {
    if (categoryOrder.includes(cat)) {
      return;
    }
    ordered.push({
      category: cat,
      label: `${categoryLabels[cat] || cat} image type`,
      types: groups[cat].sort((a, b) => a.name.localeCompare(b.name)),
    });
  });

  return ordered;
}

export async function fetchImageSequences({
  force = false,
  shareToken = null,
  intakeSlug = null,
} = {}) {
  const token = shareToken != null ? String(shareToken).trim() : getShareAccessTokenFromLocation();
  const slug = intakeSlug != null
    ? String(intakeSlug).trim()
    : (token ? '' : getIntakeOrgSlugFromLocation());
  const catalogKey = resolveCatalogKey(token || '', slug || '');
  if (!force && cachedSequences && cachedSequencesKey === catalogKey) {
    return cachedSequences;
  }
  if (!force && sequencesInflight && sequencesInflightKey === catalogKey) {
    return sequencesInflight;
  }
  const request = (async () => {
    try {
      let url = '/sequences';
      if (token) {
        url = `/briefs/api/share/${encodeURIComponent(token)}/sequences`;
      } else if (slug) {
        url = `/briefs/api/intake/${encodeURIComponent(slug)}/sequences`;
      }
      const result = await axios.get(url, SKIP_TOAST);
      if (result.status === 200 && Array.isArray(result.data)) {
        cachedSequences = result.data;
        cachedSequencesKey = catalogKey;
        return result.data;
      }
    } catch {
      /* ignore */
    }
    return cachedSequences || [];
  })();
  sequencesInflightKey = catalogKey;
  sequencesInflight = request;
  request.finally(() => {
    if (sequencesInflight === request) {
      sequencesInflight = null;
      sequencesInflightKey = null;
    }
  });
  return request;
}

export function peekCachedSequences() {
  return cachedSequences;
}

export function clearImageSequencesCache() {
  cachedSequences = null;
  cachedSequencesKey = null;
  sequencesInflight = null;
  sequencesInflightKey = null;
}

export function prefetchAddSectionCatalogs(options = {}) {
  return Promise.all([
    loadAllImageTypes(options),
    fetchImageSequences(options),
  ]);
}

export function sequenceSlots(sequence) {
  if (!sequence) {
    return [];
  }
  if (Array.isArray(sequence.slots)) {
    return sequence.slots;
  }
  return String(sequence.slots || '')
    .split(' · ')
    .map((s) => s.trim())
    .filter(Boolean);
}

export function formatSequenceSummary(sequence, { unit = 'sections' } = {}) {
  const slots = sequenceSlots(sequence);
  const labels = slots.map((slot) => {
    if (typeof slot === 'string') {
      return slot;
    }
    return slot.label || slot.name || slot.key || '';
  }).filter(Boolean);
  const preview = labels.slice(0, 3).join(', ');
  const suffix = labels.length > 3 ? '…' : '';
  return `${labels.length} ${unit} · ${preview}${suffix}`;
}

export function toImageTypeTagPayload(imageType) {
  return {
    key: imageType.key || '',
    name: imageType.name || imageType.key || 'Image',
    width: imageType.width || null,
    height: imageType.height || null,
    category: imageType.category || '',
  };
}

export function imageTypePayloadsFromSequence(sequence, allTypes) {
  return sequenceSlots(sequence).map((slot) => {
    const imageType = resolveImageTypeForSlot(slot, allTypes || []);
    if (imageType) {
      return toImageTypeTagPayload(imageType);
    }
    const label = typeof slot === 'string' ? slot : (slot.label || slot.name || 'Image');
    return {
      key: `adhoc-${label}`,
      name: label,
      width: null,
      height: null,
      category: '',
    };
  });
}

export function resolveImageTypeForSlot(slot, allTypes) {
  const label = typeof slot === 'string'
    ? slot
    : (slot.label || slot.name || slot.key || '');
  const key = typeof slot === 'object' ? (slot.key || slot.type) : null;
  if (key) {
    const byKey = allTypes.find((t) => t.key === key);
    if (byKey) {
      return byKey;
    }
    const byAlias = allTypes.find((t) => Array.isArray(t.aliases) && t.aliases.includes(key));
    if (byAlias) {
      return byAlias;
    }
  }
  const normalized = String(label).trim().toLowerCase();
  return allTypes.find((t) => t.name.trim().toLowerCase() === normalized)
    || allTypes.find((t) => t.key === normalized.replace(/\s+/g, '_'));
}

export { formatImageTypeSize };
